import {Autocomplete, CircularProgress, Stack, styled, TextField, Typography} from '@mui/material';
import {useEffect, useState} from 'react';
import {Control, Controller} from 'react-hook-form';
import {api, FullAddressDto} from '../../../global/api';
import {useDebounce} from '../../../hooks/useDebounce';
import {ProfileFormValues} from '../types/profile.types';

const Label = styled(Typography)(() => ({
  fontWeight: 500,
  fontSize: 14,
  color: '#374151',
}));

const FieldRoot = styled('div')(() => ({
  '& .MuiInputBase-root': {
    backgroundColor: '#FFFFFF',
    height: 41,
  },
  '& .MuiInputBase-input': {
    height: 41,
    padding: '0 12px',
    fontWeight: 400,
    fontSize: 16,
  },
}));

interface SimpleAddressFieldProps {
  label: string;
  name: 'fullAddress';
  control: Control<ProfileFormValues>;
}

export const SimpleAddressField = ({label, control, name}: SimpleAddressFieldProps) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<FullAddressDto[]>([]);
  const [loading, setLoading] = useState(false);
  const debouncedInputValue = useDebounce(inputValue, 350);

  useEffect(() => {
    let active = true;

    const loadAddressOptions = async () => {
      if (!debouncedInputValue.trim()) {
        setOptions([]);
        return;
      }

      try {
        setLoading(true);
        const {data} = await api.address.postAddressFindExample({
          fullAddress: debouncedInputValue,
        });

        if (active) setOptions(data || []);
      } catch {
        if (active) {
          setOptions([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadAddressOptions();

    return () => {
      active = false;
    };
  }, [debouncedInputValue]);

  return (
    <Controller
      name={name}
      control={control}
      render={({field, fieldState}) => (
        <Stack gap="4px" width="100%">
          <Label>{label}</Label>
          <FieldRoot>
            <Autocomplete<FullAddressDto, false, false, false>
              options={
                field.value &&
                !options.some(
                  (option) =>
                    option.id === field.value?.id || option.fullAddress === field.value?.fullAddress,
                )
                  ? [field.value, ...options]
                  : options
              }
              loading={loading}
              noOptionsText={
                (inputValue || field.value?.fullAddress || '').trim()
                  ? 'Нет доступных вариантов'
                  : 'Начните вводить адрес'
              }
              disablePortal
              value={field.value}
              inputValue={inputValue || field.value?.fullAddress || ''}
              isOptionEqualToValue={(option, value) =>
                option.id === value?.id || option.fullAddress === value?.fullAddress
              }
              getOptionLabel={(option) => option.fullAddress || ''}
              filterOptions={(x) => x}
              onChange={(_, value) => field.onChange(value)}
              onInputChange={(_, value, reason) => {
                setInputValue(value);
                if (reason === 'input') {
                  field.onChange(null);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  onBlur={field.onBlur}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </FieldRoot>
        </Stack>
      )}
    />
  );
};
